import { useEffect } from 'react';

export interface SEOConfig {
  title: string;
  description: string;
  image?: string;
  canonical: string;
  type?: 'website' | 'article' | 'product';
  keywords?: string;
  structuredData?: Record<string, any>;
  noIndex?: boolean;
}

export const useSEO = (config: SEOConfig) => {
  useEffect(() => {
    // Update title
    document.title = config.title;
    
    // Update or create meta description
    updateMetaTag('description', config.description);
    
    // Update keywords
    if (config.keywords) {
      updateMetaTag('keywords', config.keywords);
    }
    
    // Update Open Graph tags
    updateMetaTag('og:title', config.title, 'property');
    updateMetaTag('og:description', config.description, 'property');
    updateMetaTag('og:type', config.type || 'website', 'property');
    updateMetaTag('og:url', config.canonical, 'property');
    
    if (config.image) {
      updateMetaTag('og:image', config.image, 'property');
      updateMetaTag('og:image:alt', config.title, 'property');
      updateMetaTag('og:image:width', '1200', 'property');
      updateMetaTag('og:image:height', '630', 'property');
    }
    
    // Update Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', config.title);
    updateMetaTag('twitter:description', config.description);
    
    if (config.image) {
      updateMetaTag('twitter:image', config.image);
    }
    
    // Update canonical URL
    updateCanonicalTag(config.canonical);
    
    // Handle noindex
    if (config.noIndex) {
      updateMetaTag('robots', 'noindex,nofollow');
    } else {
      updateMetaTag('robots', 'index,follow');
    }
    
    // Add structured data
    if (config.structuredData) {
      addStructuredData(config.structuredData);
    }
    
    // Cleanup function
    return () => {
      // Remove structured data if needed
      const existingScript = document.querySelector('script[data-structured-data="true"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [config]);
};

function updateMetaTag(name: string, content: string, attribute: 'name' | 'property' = 'name') {
  let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  
  meta.content = content;
}

function updateCanonicalTag(url: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  
  link.href = url;
}

function addStructuredData(data: Record<string, any>) {
  // Remove existing structured data
  const existingScript = document.querySelector('script[data-structured-data="true"]');
  if (existingScript) {
    existingScript.remove();
  }
  
  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-structured-data', 'true');
  script.textContent = JSON.stringify(data, null, 2);
  document.head.appendChild(script);
}

// SEO configurations for each page
export const seoConfigs = {
  home: {
    title: "Sangam Panta | Sound Designer & Film Composer | Nepal, Estonia & Europe",
    description: "Professional sound designer and film composer specializing in cinematic audio, Dolby Atmos mixing, and immersive soundscapes. Based in Nepal, Estonia, and serving clients across Europe including Portugal.",
    image: "/01-WebsiteFiles/Featured Photos/Sangam Panta - Sound Designer, Film Composer.webp",
    canonical: "https://sangampanta.codeberg.page/",
    type: "website" as const,
    keywords: "sound design Nepal, sound design Estonia, sound design Portugal, sound design Europe, film composer Nepal, film composer Estonia, film composer Portugal, film composer Europe, Dolby Atmos mixing, cinematic audio post-production, immersive sound design, audio library management, theatrical audio delivery, DCP audio tools, macOS audio software, film sound design, film sound recording, dialogue editing, foley recording, sound mixing, audio mastering, sound editing, audio editing, sound design for films, sound design for videos, sound design for podcasts, DCP making, sound designer for independent films Nepal, Dolby Atmos mixing engineer Estonia, audio post-production tools for filmmakers, professional sound library organizer macOS, theatrical audio delivery for films, DCP audio tools for filmmakers, immersive sound design for films, sound mixing for films, audio mastering for films, dialogue editing for films, foley recording for films, sound design for films Portugal, sound design for films Europe, film composer for films Portugal, film composer for films Europe, Nepal, Estonia, Portugal, Europe, cinematic audio, sound mixing",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Sangam Panta",
      "jobTitle": ["Sound Designer", "Film Composer"],
      "description": "Professional sound designer and film composer specializing in cinematic audio and immersive soundscapes",
      "url": "https://sangampanta.codeberg.page/",
      "image": "https://sangampanta.codeberg.page/01-WebsiteFiles/Featured Photos/Sangam Panta - Sound Designer, Film Composer.webp",
      "sameAs": [
        "https://sangampanta.github.io/SangamPanta"
      ],
      "knowsAbout": [
        "Sound Design",
        "Film Composition",
        "Dolby Atmos",
        "Audio Post-Production",
        "Immersive Audio"
      ],
      "worksFor": {
        "@type": "Organization",
        "name": "Independent Film & Audio Projects"
      },
      "areaServed": [
        {
          "@type": "Country",
          "name": "Nepal"
        },
        {
          "@type": "Country", 
          "name": "Estonia"
        },
        {
          "@type": "Country",
          "name": "Portugal"
        },
        {
          "@type": "Continent",
          "name": "Europe"
        }
      ]
    }
  },
  
  soundLibraryExplorer: {
    title: "SoundLibraryExplorer | macOS Audio Management Tool | Sangam Panta",
    description: "Professional macOS audio library manager for sound designers. Organize, tag, and preview massive sound libraries with privacy-first workflows. One-time purchase, lifetime access.",
    image: "/01-WebsiteFiles/SoundLibraryExplorer/SLE-About Section.webp",
    canonical: "https://sangampanta.codeberg.page/soundlibraryexplorer",
    type: "product" as const,
    keywords: "audio management, sound library, macOS app, sound designer tools, audio organization, waveform preview, metadata search, privacy-first audio, audio library management, macOS audio software, professional sound library organizer macOS, sound editing, audio editing, sound design for podcasts",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "SoundLibraryExplorer",
      "description": "Professional macOS audio library manager for organizing, tagging, and previewing massive sound libraries with privacy-first workflows",
      "url": "https://sangampanta.codeberg.page/soundlibraryexplorer",
      "image": "https://sangampanta.codeberg.page/01-WebsiteFiles/SoundLibraryExplorer/SLE-About Section.webp",
      "applicationCategory": "MultimediaApplication",
      "operatingSystem": "macOS",
      "offers": {
        "@type": "Offer",
        "price": "29.99",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Person",
          "name": "Sangam Panta"
        }
      },
      "featureList": [
        "Smart audio organization with automatic tagging",
        "Advanced metadata search and filtering",
        "Visual waveform preview",
        "Batch processing capabilities",
        "Duplicate detection",
        "Privacy-first workflow"
      ]
    }
  },
  
  cinemmersePro: {
    title: "Cinemmerse Pro | Theatrical Audio Delivery Tool | SMPTE Compliant",
    description: "Professional theatrical audio delivery tool for converting IMF IAB masters to DCI-compliant DCP IAB assets. SMPTE RDD57 compliance for cinema distribution.",
    image: "/01-WebsiteFiles/CinemmersePro/CinemmersePro-About.webp",
    canonical: "https://sangampanta.codeberg.page/cinemmersepro",
    type: "product" as const,
    keywords: "theatrical delivery, DCP, SMPTE, cinema audio, Dolby Atmos, IMF IAB, audio conversion, film distribution, cinema processors, theatrical audio delivery, DCP audio tools, DCP making, DCP audio tools for filmmakers, theatrical audio delivery for films, immersive sound design for films",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Cinemmerse Pro",
      "description": "Professional theatrical audio delivery tool for converting IMF IAB masters to DCI-compliant DCP IAB assets with SMPTE RDD57 compliance",
      "url": "https://sangampanta.codeberg.page/cinemmersepro",
      "image": "https://sangampanta.codeberg.page/01-WebsiteFiles/CinemmersePro/CinemmersePro-About.webp",
      "applicationCategory": "MultimediaApplication",
      "operatingSystem": "macOS",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Person",
          "name": "Sangam Panta"
        }
      },
      "featureList": [
        "Lossless transparency using SMPTE DLC",
        "Cinematic Aurora UI design",
        "Zero-config detection",
        "Theatrical lockdown enforcement",
        "Universal compatibility with cinema processors",
        "Real-time 128-channel budget monitoring"
      ],
      "requirements": "macOS 13 or newer"
    }
  },
  
  contact: {
    title: "Contact Sangam Panta | Sound Designer & Film Composer | Nepal, Estonia, Portugal",
    description: "Get in touch with Sangam Panta for sound design, film composition, and audio post-production projects. Available for collaborations in Nepal, Estonia, Portugal, and across Europe.",
    canonical: "https://sangampanta.codeberg.page/contact",
    type: "website" as const,
    keywords: "contact sound designer Nepal, sound designer Estonia, sound designer Portugal, sound designer Europe, hire film composer, audio collaboration, sound design projects, film music composition, Nepal Estonia Portugal Europe, sound designer for independent films Nepal, Dolby Atmos mixing engineer Estonia, film composer for films Portugal, film composer for films Europe, sound design for films Portugal, sound design for films Europe",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Sangam Panta",
      "description": "Contact page for sound design and film composition collaborations",
      "url": "https://sangampanta.codeberg.page/contact",
      "mainEntity": {
        "@type": "Person",
        "name": "Sangam Panta",
        "jobTitle": ["Sound Designer", "Film Composer"]
      }
    }
  },
  
  credits: {
    title: "Film Credits | Sangam Panta - Sound Designer & Film Composer",
    description: "Complete filmography and credits for Sangam Panta as sound designer and film composer. Features work on independent films, documentaries, and international collaborations.",
    canonical: "https://sangampanta.codeberg.page/credits",
    type: "website" as const,
    keywords: "film credits, sound design portfolio, film composer credits, cinematography, audio post-production credits, independent films, film sound design, film sound recording, sound mixing for films, audio mastering for films, dialogue editing for films, foley recording for films",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Film Credits",
      "description": "Complete filmography and credits for sound design and film composition work",
      "url": "https://sangampanta.codeberg.page/credits"
    }
  },
  
  music: {
    title: "Music Compositions | Sangam Panta - Film Composer",
    description: "Original music compositions and scores by Sangam Panta. Featuring film scores, electronic music, and collaborative compositions for cinema and media projects.",
    canonical: "https://sangampanta.codeberg.page/music",
    type: "website" as const,
    keywords: "film music, original compositions, music scores, electronic music, film composer portfolio, soundtrack music, sound design for videos, film composer for films Portugal, film composer for films Europe",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Music Compositions",
      "description": "Original music compositions and film scores",
      "url": "https://sangampanta.codeberg.page/music"
    }
  },
  
  downloads: {
    title: "Downloads | Sangam Panta - Audio Tools & Resources",
    description: "Download professional audio tools including SoundLibraryExplorer, DCP tools, timecode utilities, and other resources for sound designers and film composers.",
    canonical: "https://sangampanta.codeberg.page/downloads",
    type: "website" as const,
    keywords: "audio tools download, sound design software, DCP tools, timecode utilities, audio post-production tools, macOS audio apps, audio post-production tools for filmmakers, DCP audio tools for filmmakers",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Downloads",
      "description": "Professional audio tools and resources for download",
      "url": "https://sangampanta.codeberg.page/downloads"
    }
  }
};
